import type { Any, Arr, Coll, Obj, Seq } from '../interface'
import type { Atom, EffectRef, FunctionLike, RegularExpression } from '../parser/types'
import type { SourceCodeInfo } from '../tokenizer/token'
import { getAssertionError } from '../utils/getAssertionError'
import { isPersistentMap, isPersistentVector } from '../utils/persistent'
import { ATOM_SYMBOL, EFFECT_SYMBOL, REGEXP_SYMBOL } from '../utils/symbols'
import { isDvalaFunction } from './dvalaFunction'

export function isAny(value: unknown): value is Any {
  // TODO weak test
  return value !== undefined
}
export function asAny(value: unknown, sourceCodeInfo?: SourceCodeInfo): Any {
  assertAny(value, sourceCodeInfo)
  return value
}
export function assertAny(value: unknown, sourceCodeInfo?: SourceCodeInfo): asserts value is Any {
  if (!isAny(value))
    throw getAssertionError('not undefined', value, sourceCodeInfo)
}

export function isSeq(value: unknown): value is Seq {
  return isPersistentVector(value) || typeof value === 'string'
}
export function asSeq(value: unknown, sourceCodeInfo?: SourceCodeInfo): Seq {
  assertSeq(value, sourceCodeInfo)
  return value
}
export function assertSeq(value: unknown, sourceCodeInfo?: SourceCodeInfo): asserts value is Seq {
  if (!isSeq(value))
    throw getAssertionError('string or array', value, sourceCodeInfo)
}

export function isArr(value: unknown): value is Arr {
  return isPersistentVector(value)
}
export function asArr(value: unknown, sourceCodeInfo?: SourceCodeInfo): Arr {
  assertArr(value, sourceCodeInfo)
  return value
}
export function assertArr(value: unknown, sourceCodeInfo?: SourceCodeInfo): asserts value is Arr {
  if (!isArr(value))
    throw getAssertionError('array', value, sourceCodeInfo)
}

export function isObj(value: unknown): value is Obj {
  return isPersistentMap(value)
}
export function asObj(value: unknown, sourceCodeInfo?: SourceCodeInfo): Obj {
  assertObj(value, sourceCodeInfo)
  return value
}
export function assertObj(value: unknown, sourceCodeInfo?: SourceCodeInfo): asserts value is Obj {
  if (!isObj(value))
    throw getAssertionError('object', value, sourceCodeInfo)
}

export function isColl(value: unknown): value is Coll {
  return isSeq(value) || isObj(value)
}
export function asColl(value: unknown, sourceCodeInfo?: SourceCodeInfo): Coll {
  assertColl(value, sourceCodeInfo)
  return value
}
export function assertColl(value: unknown, sourceCodeInfo?: SourceCodeInfo): asserts value is Coll {
  if (!isColl(value))
    throw getAssertionError('string, array or object', value, sourceCodeInfo)
}

export function isRegularExpression(regexp: unknown): regexp is RegularExpression {
  if (regexp === null || typeof regexp !== 'object')
    return false

  return !!(regexp as RegularExpression)[REGEXP_SYMBOL]
}
export function asRegularExpression(value: unknown, sourceCodeInfo?: SourceCodeInfo): RegularExpression {
  assertRegularExpression(value, sourceCodeInfo)
  return value
}
export function assertRegularExpression(
  value: unknown,
  sourceCodeInfo?: SourceCodeInfo,
): asserts value is RegularExpression {
  if (!isRegularExpression(value))
    throw getAssertionError('RegularExpression', value, sourceCodeInfo)
}

export function isStringOrRegularExpression(value: unknown): value is string | RegularExpression {
  return isRegularExpression(value) || typeof value === 'string'
}
export function asStringOrRegularExpression(
  value: unknown,
  sourceCodeInfo?: SourceCodeInfo,
): string | RegularExpression {
  assertStringOrRegularExpression(value, sourceCodeInfo)
  return value
}
export function assertStringOrRegularExpression(
  value: unknown,
  sourceCodeInfo?: SourceCodeInfo,
): asserts value is string | RegularExpression {
  if (!isStringOrRegularExpression(value))
    throw getAssertionError('string or RegularExpression', value, sourceCodeInfo)
}

export function isAtom(value: unknown): value is Atom {
  if (value === null || typeof value !== 'object')
    return false
  return !!(value as Atom)[ATOM_SYMBOL]
}

export function isEffect(value: unknown): value is EffectRef {
  if (value === null || typeof value !== 'object')
    return false

  return !!(value as EffectRef)[EFFECT_SYMBOL]
}
export function asEffect(value: unknown, sourceCodeInfo?: SourceCodeInfo): EffectRef {
  assertEffect(value, sourceCodeInfo)
  return value
}
export function assertEffect(
  value: unknown,
  sourceCodeInfo?: SourceCodeInfo,
): asserts value is EffectRef {
  if (!isEffect(value))
    throw getAssertionError('Effect', value, sourceCodeInfo)
}

function isFunctionLike(value: unknown): value is FunctionLike {
  if (typeof value === 'number')
    return true
  if (isColl(value))
    return true
  if (isDvalaFunction(value))
    return true

  return false
}
export function asFunctionLike(value: unknown, sourceCodeInfo?: SourceCodeInfo): FunctionLike {
  assertFunctionLike(value, sourceCodeInfo)
  return value
}
export function assertFunctionLike(value: unknown, sourceCodeInfo?: SourceCodeInfo): asserts value is FunctionLike {
  if (!isFunctionLike(value))
    throw getAssertionError('FunctionLike', value, sourceCodeInfo)
}
